<template>
  <div
    v-if="post"
    class="layer-item post"
    :class="postClassName"
    @click.stop="onSelect"
  >
    <template v-if="isVideoPreview">
      <video
        class="media video"
        type="video/mp4"
        muted
        :src="videoUrl"
        alt="video"
        title="Video"
      />

      <button
        class="tb-btn tb-btn-contained on-image play-btn"
        @click.stop="onClickPlay"
      >
        <span
          class="icon"
          :class="`icon-${videoPlaying ? 'pause' : 'play'}`"
        />
      </button>
    </template>

    <div
      v-else-if="post.videos && post.videos.length"
      class="media"
      :style="{ backgroundImage: 'url(' + photoUrl + ')' }"
    >
      <div
        v-if="post.videos[0].type === 'gif'"
        class="tb-btn tb-btn-contained on-image play-btn gif"
      >
        gif
      </div>

      <template v-else>
        <button
          class="tb-btn tb-btn-contained on-image play-btn"
          @click.stop="onClickPlay"
        >
          <span
            class="icon"
            :class="`icon-${videoPlaying ? 'pause' : 'play'}`"
          />
        </button>
      </template>
    </div>

    <div
      v-else-if="post.photos && post.photos.length"
      class="media"
      :style="{ backgroundImage: 'url(' + photoUrl + ')' }"
    />

    <div v-if="!isStories" v-bind:class="verified" class="meta-author clearfix">
      <template v-if="post.network === 'instagram'">
        <span v-if="post.user_name" class="handle">@<span v-html="post.user_name"></span></span>
        <span v-else class="handle">Instagram user</span>
      </template>
      <template v-if="post.network === 'share-it'">
        <span v-if="post.user_name" class="handle"><span v-html="post.user_name"></span></span>
      </template>
      <template v-if="post.network === 'reddit'">
        <span
          v-if="post.service_meta.subreddit"
          class="subreddit"
        >
          in r/<span v-html="post.service_meta.subreddit"></span>
        </span>
        <span class="handle">by u/<span v-html="post.user_real_name"></span></span>
      </template>
      <template v-if="post.network !== 'instagram' && post.network !== 'reddit'">
        <span class="author" v-html="post.user_real_name"></span>
        <span
          v-if="post.network === 'twitter' || post.network === 'tiktok'"
          class="handle"
        >
          @<span v-html="post.user_name"></span>
        </span>
      </template>
    </div>

    <div v-if="!hasMedia" class="message">
      <div
        class="post-title"
        v-html="post.title"
        v-if="(!post.tgb_meta || !post.tgb_meta.isComment) && post.title"
      />

      <div v-if="isChat" class="post-text" v-html="postHtml"></div>
      <div v-else class="post-text" v-html="post.text"></div>
    </div>

    <div class="origin-flag" v-bind:class="networkIconClass"></div>

    <dropdown position="top right" class="more-actions" ref="dropdown">
      <template slot="trigger">
        <button class="tb-btn tb-btn-text sm medium-em" title="More options">
          <span class="icon icon-more-vertical"></span>
        </button>
      </template>

      <template slot="dropdown-body">
        <button
          class="dropdown-list-item"
          role="button"
          @click.stop="onRemove"
        >
          <span class="dropdown-list-item-label warning">Remove</span>
        </button>
      </template>
    </dropdown>
  </div>

  <div
    v-else
    class="layer-item deleted"
    :class="[{ active: (isCurrentPost || isCurrentSamplePost) }]"
  >
    <div class="deleted-message">This post has been deleted or unfeatured.</div>
    <button
      class="tb-btn tb-btn-text sm medium-em actions"
      title="Remove item from playlist"
      @click.stop.prevent="onRemove"
    >
      <span class="icon icon-minus-circle" />
    </button>
  </div>
</template>

<script>
import TimelineMixins from '../../../mixins/timeline-mixins';

const PostUtils = require('@tagboard/helpers/post-utils');
const MediaProxy = require('@tagboard/helpers/media-proxy');

export default {
  name: 'TimelinePostComponent',
  mixins: [TimelineMixins],

  props: {
    // Social post
    item: {
      type: Object,
      default: () => null,
    },

    // Parent layout (graphic, theme, etc.)
    layout: {
      type: Object,
      default: 'theme',
    },

    // Post to highlight. This is the post that will be shown when the theme/graphic goes live.
    queuedPost: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      proxyAttempt: false,
    };
  },

  mounted() {
    if (this.post && MediaProxy.usingNetwork(this.post.network)) {
      // Load image, and if it fails switch to the proxy
      const img = new Image();
      img.onerror = () => { this.proxyAttempt = true; };

      img.src = this.photoUrl;
    }
  },

  methods: {
    onSelect() {
      this.$emit('select', {
        parent: this.layout.timeline_id,
        item: this.item,
      });
    },

    /**
    * Toggles videoPlaying on current post when clicked.
    *
    * If the user clicks the play button on a post that is NOT live,
    * then set that post to live and set `videoPlaying` to true.
    *
    * If layout isn't live, then queue the post but don't touch `videoPlaying`
    */
    onClickPlay() {
      if (!this.isCurrentLayout && !this.isCurrentSampleLayout) {
        // Timeline item isn't live, so just queue the post for later and get out of this mess
        this.onSelect();
        return;
      }

      // There's probably a clean way to separate preview and live, but my brain couldn't get it to work.
      // So I'm just going to treat it like three different states (both, live, and preview)

      if (this.isCurrentLayout && this.isCurrentSampleLayout) { // Live & Preview
        // Graphic is in Live and Preview together
        if (this.isCurrentPost) {
          // Toggle `videoPlaying` for existing post
          this.$emit('toggle-video');

          // Sync state with preview `videoPlaying`
          if (this.previewVideoPlaying !== this.liveVideoPlaying) {
            this.$emit('toggle-preview-video');
          }
        } else {
          // Switch to other post and set `videoPlaying` to true
          this.onSelect();
          this.$emit('play-video');
          this.$emit('play-preview-video');
        }
      } else if (this.isCurrentLayout) { // Live
        // Graphic is only in Live
        if (this.isCurrentPost) {
          // Toggle `videoPlaying` for existing post
          this.$emit('toggle-video');
        } else {
          // Switch to other post and set `videoPlaying` to true
          this.onSelect();
          this.$emit('play-video');
        }
      } else if (this.isCurrentSampleLayout) { // Preview
        // Graphic is only in preview
        if (this.isCurrentSamplePost) {
          // Toggle `videoPlaying` for existing post (preview)
          this.$emit('toggle-preview-video');
        } else {
          // Switch to other post and set `videoPlaying` to true
          this.onSelect();
          this.$emit('play-preview-video');
        }
      }
    },

    onRemove() {
      this.$emit('delete', {
        timelineId: this.layout?.timeline_id,
        subIndex: this.item?.timeline_id,
      });
    },
  },

  computed: {
    postClassName() {
      const {
        post,
        hasMedia,
        isCurrentPost,
        isCurrentSamplePost,
        isQueuedPost,
      } = this;

      return [
        post.network,
        post.post_type,
        hasMedia,
        {
          active: (isCurrentPost || isCurrentSamplePost),
        },
        {
          queued: isQueuedPost,
        },
      ];
    },

    post() {
      return this.item && this.$store.state.posts.lookupTable[this.item.post_id];
    },

    isQueuedPost() {
      return ((this.queuedPost === this.item.timeline_id) && !(this.isCurrentPost || this.isCurrentSamplePost));
    },

    isGif() {
      if (!this.post.videos && this.post.videos.length) {
        return false;
      }

      return this.post.videos[0].type === 'gif';
    },

    isVideo() {
      return this.post.post_type === 'video' && (this.post.videos && this.post.videos.length);
    },

    isChat() {
      return this.post.post_type === 'chat';
    },

    photoUrl() {
      if (this.post.photos && this.post.photos.length) {
        let url = this.post.photos[0].m;
        if (this.post.network === 'facebook') {
          url = this.post.photos[0].l || this.post.photos[0].m;
        } else if (this.post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(this.post, 'm');
        }

        return this.proxyAttempt ? MediaProxy.genUrl(url) : url;
      }

      if (this.post.videos && this.post.videos.length) {
        let url = this.post.videos[0].p;
        if (this.post.network === 'youtube') {
          url = this.post.videos[0].t;
        } else if (this.post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(this.post, 'm');
        }

        return this.proxyAttempt ? MediaProxy.genUrl(url) : url;
      }

      return '';
    },

    hasMedia() {
      const hasPhotos = this.post.photos && this.post.photos.length;
      const hasVideos = this.post.videos && this.post.videos.length;
      if (hasPhotos || hasVideos) {
        return 'has-media';
      }

      return '';
    },

    networkIconClass() {
      if (!this.post.network) {
        return '';
      }

      if (this.post.network === 'instagram-stories') {
        return 'icon icon-instagram';
      }

      if (this.post.network === 'twitter') {
        return 'icon icon-twitterx';
      }

      return `icon icon-${this.post.network}`;
    },

    isStories() {
      return ['snapchat', 'instagram-stories'].includes(this.post.network);
    },

    hasAvatar() {
      let hasAvatar = true;

      switch (this.post.network) {
        case 'instagram':
          // Instagram posts don't display avatars
          hasAvatar = false;
          break;
        case 'reddit':
          // Reddit doesn't display avatars
          hasAvatar = false;
          break;
        case 'facebook':
          if (this.post.tgb_meta && this.post.tgb_meta.isComment) {
            // Facebook Comments don't display avatars
            hasAvatar = false;
          }

          break;
        default:
          break;
      }

      return hasAvatar;
    },

    noAvatar() {
      let hasAvatar = true;

      switch (this.post.network) {
        case 'instagram':
          // Instagram posts don't display avatars
          hasAvatar = false;
          break;
        case 'reddit':
          // Reddit doesn't display avatars
          hasAvatar = false;
          break;
        case 'facebook':
          if (this.post.tgb_meta && this.post.tgb_meta.isComment) {
            // Facebook Comments don't display avatars
            hasAvatar = false;
          }

          break;
        default:
          break;
      }

      return hasAvatar ? '' : 'no-avatar';
    },

    verified() {
      return {
        verified: this.post.service_meta && this.post.service_meta.user_verified,
        gov: this.post.service_meta && this.post.service_meta.user_verified && this.post.service_meta.verified_type === 'government',
        org: this.post.service_meta && this.post.service_meta.user_verified && this.post.service_meta.verified_type === 'business',
      };
    },

    videoType() {
      if (this.post.videos && this.post.videos[0].type) {
        return this.post.videos[0].type;
      }

      return '';
    },

    videoUrl() {
      const isVideoType = this.post.post_type === 'video';
      const hasVideos = this.post.videos && this.post.videos.length;
      if (isVideoType && hasVideos) {
        return this.post.videos[0].m;
      }

      return undefined;
    },

    isVideoPreview() {
      const isVideoPost = this.post.post_type === 'video';
      const hasVideoUrl = !!this.videoUrl;
      const hasNoVideoPosterUrl = !this.photoUrl;
      const isMp4 = this.videoType === 'mp4';
      return isVideoPost && hasVideoUrl && hasNoVideoPosterUrl && isMp4;
    },

    postHtml() {
      return this.post.html || this.post.text;
    },
  },
};
</script>
